import { Injector } from '@angular/core';
import {
  APP_ZONES,
  ActionButtonOutcomesType,
  BaseWidgetProperties,
  ConfirmationClaimType,
  PaymentStatusScreen,
} from '@finxone-platform/shared/sys-config-types';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { map, take } from 'rxjs';
import { PaymentDeclined, PaymentSuccess } from '../../../actions/payment.action';
import { ConfigService } from '../../../services/config-service/config-service.service';
import { ConfirmationService } from '../../../services/confirmation-service/confirmation-service';
import { ProjectSettingsState } from '../../../state/project-settings.state';
import { handleRedirection } from '../cta-redirection.utils';
import { submitInitiatorPayment } from './submit-initiator-payment';

/**
 * Initiates the domestic payment approval process for a widget.
 *
 * This function handles the initiation of a domestic payment approval by
 * dispatching actions and navigating to appropriate screens based on the
 * payment outcome. It checks if SMS authentication is enabled and redirects
 * accordingly. If SMS authentication is not enabled, it submits the payment
 * directly.
 *
 * @param widgetProperties - The properties of the widget initiating the payment.
 * @param injector - The Angular injector used to retrieve services.
 */
export function paymentApproverInitiateDomesticPayment(
  widgetProperties: BaseWidgetProperties,
  injector: Injector,
) {
  const _store = injector.get(Store);
  const _actions = injector.get(Actions);
  const configService = injector.get(ConfigService);
  const confirmationService = injector.get(ConfirmationService);
  const isSmsAuthEnabled = _store.selectSnapshot(
    ProjectSettingsState.getProjectSettings,
  ).smsAuthenticationEnabled;

  if (!widgetProperties['zoneToNavigate']) {
    widgetProperties['zoneToNavigate'] = APP_ZONES.PAYMENT;
  }
  const parsedWidgetProperties: BaseWidgetProperties = JSON.parse(JSON.stringify(widgetProperties));

  _actions
    .pipe(
      ofActionDispatched(PaymentSuccess, PaymentDeclined),
      take(1),
      map((res) => {
        const type =
          res instanceof PaymentSuccess ? ActionButtonOutcomesType.SUCCESS : ActionButtonOutcomesType.FAILURE;
        parsedWidgetProperties['urlToNavigate'] =
          type === ActionButtonOutcomesType.SUCCESS
            ? `${PaymentStatusScreen.PAYMENT_SUCCESS}-initiator`
            : `payment-failed-initiator`;
        handleRedirection(parsedWidgetProperties, injector, type);
      }),
    )
    .subscribe();
  const processingPageUrl = configService.reRouteIfModal(
    widgetProperties['zoneToNavigate'],
    'payment-processing-initiator',
  );
  if (parsedWidgetProperties?.actionNavigationConfigurations?.[ActionButtonOutcomesType.PROCESSING]) {
    widgetProperties['urlToNavigate'] =
      parsedWidgetProperties.actionNavigationConfigurations[
        ActionButtonOutcomesType.PROCESSING
      ].selectedPageUrl;
  }
  if (isSmsAuthEnabled) {
    const subscription = confirmationService.checkConfirmation(processingPageUrl).subscribe(() => {
      submitInitiatorPayment(injector, parsedWidgetProperties);
      subscription.unsubscribe();
    });
    confirmationService.confirmAction(ConfirmationClaimType.INITIATE_PAYMENT, processingPageUrl);
  } else {
    submitInitiatorPayment(injector, parsedWidgetProperties);
  }
}
